import { Icon, Pagination } from '@petsmart-ui/sparky';
import type { MouseEvent } from 'react';
import { usePagination } from 'react-instantsearch';

import type { ProductPaginationProps } from './types';
import { useResponsiveDisplay } from './useResponsiveDisplay';

import '@/components/ProductPagination/ProductPagination.scss';

/* TODO: Check if the additional logic can be extracted to the sparky Pagination component instead of adding it here.
E.g.: custom back/next arrows could be replaced if adding onClick behavior on sparky side */
export default function ProductPagination({ containerRef }: ProductPaginationProps) {
    const { currentRefinement, refine, pages, isFirstPage, isLastPage, nbPages } = usePagination();

    const { showFirstEllipsis, showLastEllipsis, pagesToShowBetweenEllipsis } = useResponsiveDisplay({
        pages,
        currentRefinement,
        nbPages,
    });

    const firstPage = 0;
    const lastPage = nbPages - 1;

    const scrollToTop = () => {
        setTimeout(() => {
            if (containerRef?.current) {
                containerRef.current.scrollIntoView({
                    behavior: 'instant',
                });
            }
        }, 100);
    };

    return (
        <Pagination removePrevButton removeNextButton className="product-pagination" data-testid="paginate">
            {nbPages > 1 && (
                <Pagination.Item
                    disabled={isFirstPage}
                    data-testid="paginate-first-item"
                    onClick={(event: MouseEvent) => {
                        event.preventDefault();
                        if (!isFirstPage) {
                            refine(currentRefinement - 1);
                        }

                        scrollToTop();
                    }}
                >
                    <Icon name="chevron-left" size="md" />
                </Pagination.Item>
            )}
            {showFirstEllipsis && (
                <>
                    <Pagination.Item
                        key={firstPage}
                        onClick={(event: MouseEvent) => {
                            event.preventDefault();
                            if (currentRefinement !== firstPage) {
                                refine(firstPage);
                            }

                            scrollToTop();
                        }}
                        isActive={firstPage === currentRefinement}
                        data-testid={`paginate-item-${firstPage}`}
                    >
                        {firstPage + 1}
                    </Pagination.Item>
                    <Pagination.Item
                        key="start_ellipsis"
                        isActive={false}
                        data-testid="paginate-item-start-elipsis"
                        divide
                    />
                </>
            )}
            {pagesToShowBetweenEllipsis.map(page => (
                <Pagination.Item
                    key={page}
                    onClick={(event: MouseEvent) => {
                        event.preventDefault();
                        if (currentRefinement !== page) {
                            refine(page);
                        }

                        scrollToTop();
                    }}
                    isActive={page === currentRefinement}
                    data-testid={`paginate-item-${page}`}
                >
                    {page + 1}
                </Pagination.Item>
            ))}
            {showLastEllipsis && (
                <>
                    <Pagination.Item
                        key="end_ellipsis"
                        isActive={false}
                        data-testid="paginate-item-end-elipsis"
                        divide
                    />
                    <Pagination.Item
                        key={lastPage}
                        onClick={(event: MouseEvent) => {
                            event.preventDefault();
                            if (currentRefinement !== lastPage) {
                                refine(lastPage);
                            }

                            scrollToTop();
                        }}
                        isActive={lastPage === currentRefinement}
                        data-testid={`paginate-item-${lastPage}`}
                    >
                        {lastPage + 1}
                    </Pagination.Item>
                </>
            )}
            {nbPages > 1 && (
                <Pagination.Item
                    onClick={(event: MouseEvent) => {
                        event.preventDefault();
                        if (!isLastPage) {
                            refine(currentRefinement + 1);
                        }

                        scrollToTop();
                    }}
                    disabled={isLastPage}
                    data-testid="paginate-last-item"
                >
                    <Icon name="chevron-right" size="md" />
                </Pagination.Item>
            )}
        </Pagination>
    );
}
